import { z } from 'zod/v4';

import { buttonAnimationSchema } from './button-animation.schema';
import { normalizeButtonWidgetArgs } from './button-widget-args.normalization';
import { partialButtonItemSchema } from './partial-button-item.schema';

/**
 * `data` is a PARTIAL button entity — the exact same `partialButtonItemSchema` that
 * `create_button_widget` uses for its `data.buttons[]` items, kept identical on purpose: models
 * have been observed generalizing `create_button_widget`'s `data` wrapper onto this tool too
 * (sending `data` here even before this field existed), so giving both tools the same field
 * surface plays to that instinct instead of fighting it.
 */
/**
 * `data` is the partial button ITEM plus the one field that is NOT a property of a button.
 *
 * The animation sits beside `buttons` on the widget's own data and plays over the whole group, so
 * strictly it does not belong in an item patch. It is declared here anyway, and `applyButtonWidgetUpdate`
 * peels it back off, for two reasons: `create_button_widget` takes it under `data` (there `data` IS
 * the widget data), and so do the text and image tools. A model that has learnt `data.animation`
 * everywhere else would send it here too — and against a bare item schema that key is stripped in
 * silence, which is a call that reports success and animates nothing.
 *
 * `partialButtonItemSchema` itself is left alone: it is shared with `create_button_widget`'s
 * `data.buttons[]`, where a per-item animation would be a field the entity does not have.
 */
const updateButtonWidgetDataSchema = partialButtonItemSchema.extend({
    animation: buttonAnimationSchema.optional(),
});

const updateButtonWidgetObjectSchema = z.object({
    widgetId: z.string().describe('Id of the button widget to update.'),
    data: updateButtonWidgetDataSchema.describe(
        'Partial button entity — only the fields you provide are changed, everything else on the button is ' +
            'left as-is. "animation" is the exception: it belongs to the whole button group, not to one button.'
    ),
});

/**
 * Mirror image of `create_button_widget`'s normalization (shared scaffolding lives in
 * `normalizeButtonWidgetArgs`): models have been observed sending a flat `label`/`text`/
 * `buttonText` (instead of `data.label`) — and copying create's `{ buttons: [{...}] }` wrapper
 * onto this tool (its own earlier create call and result both show `buttons`, so it
 * pattern-matches that instead of this tool's flat partial-item shape). A bare `z.object()`
 * strips the unrecognized `buttons` key SILENTLY, turning the call into an empty no-op patch
 * that reports success — fold the first item's fields into the patch instead.
 */
function normalizeUpdateButtonWidgetArgs(raw: unknown): unknown {
    return normalizeButtonWidgetArgs(raw, (args, data) => {
        if (Array.isArray(data['buttons'])) {
            const [firstButton] = data['buttons'] as unknown[];
            delete data['buttons'];
            if (firstButton && typeof firstButton === 'object' && !Array.isArray(firstButton)) {
                data = { ...(firstButton as Record<string, unknown>), ...data };
            }
        }

        if (data['label'] === undefined) {
            const flatLabel = data['text'] ?? data['buttonText'] ?? args['label'] ?? args['text'] ?? args['buttonText'];
            if (typeof flatLabel === 'string') {
                data['label'] = flatLabel;
            }
        }

        delete data['text'];
        delete data['buttonText'];

        return data;
    });
}

export const updateButtonWidgetArgsSchema = z.preprocess(
    normalizeUpdateButtonWidgetArgs,
    updateButtonWidgetObjectSchema
);
